import { useState, useEffect, useCallback } from 'react'
import { lumi } from '../lib/lumi'
import toast from 'react-hot-toast'
import Papa from 'papaparse'

interface MicrocuencaCompleto {
  _id?: string
  fid: number
  CODIGO: string
  Nombre_De_Microcuenca: string
  Municipio: string
  Superficie_agricola?: number
  Superficie_Microcuenca?: number
  Superficie_pasto?: number
  Superficie_forestal?: number
  Densidad_de_poblacion?: number
  Poblacion_total?: number
  Indice_de_marginacion?: number
  Grado_de_indice_de_marginacion?: string
  Indice_de_rezago?: number
  Clase_de_rezago?: string
  Precipitacion?: number
  Temperatura_media?: number
  Altitud?: number
  Pendiente?: number
  Superficie_deforestada?: number
  Erosion?: number
  Categoria_de_erosion?: string
  Nivel_de_erosion?: number
  Ponderacion_de_escurrimiento?: number
  Hectareas_en_ANP?: number
  createdAt?: string
  updatedAt?: string
  creator?: string
}

export const useMicrocuencasCompletoCRUD = () => {
  const [microcuencasCompleto, setMicrocuencasCompleto] = useState<MicrocuencaCompleto[]>([])
  const [loading, setLoading] = useState(false)
  const [uploading, setUploading] = useState(false)

  const fetchMicrocuencasCompleto = useCallback(async () => {
    setLoading(true)
    try {
      const response = await lumi.entities.microcuencas_completo.list({
        sort: { createdAt: -1 }
      })
      setMicrocuencasCompleto(response.list || [])
    } catch (error: any) {
      console.error('Error al cargar microcuencas completo:', error)
      toast.error('Error al cargar los datos completos')
    } finally {
      setLoading(false)
    }
  }, [])

  const uploadCSVFromURL = async (url: string) => {
    setUploading(true)
    try {
      // Intentar fetch sin restricciones CORS
      let response: Response
      try {
        response = await fetch(url, {
          mode: 'no-cors',
          headers: {
            'Accept': 'text/csv,text/plain,application/csv'
          }
        })
      } catch (corsError) {
        // Si falla, intentar sin modo CORS
        response = await fetch(url)
      }
      
      if (!response.ok && response.status !== 0) {
        throw new Error(`Error al descargar el archivo: ${response.status} ${response.statusText}`)
      }
      
      const csvText = await response.text()
      
      if (!csvText || csvText.trim().length === 0) {
        throw new Error('El archivo descargado está vacío')
      }
      
      return new Promise<void>((resolve, reject) => {
        Papa.parse<any>(csvText, {
          header: true,
          skipEmptyLines: true,
          complete: async (results) => {
            try {
              if (results.errors.length > 0) {
                console.warn('Errores en el CSV:', results.errors)
              }

              const processedData = results.data.map((row: any) => ({
                fid: parseInt(row.fid) || 0,
                CODIGO: row.CODIGO || '',
                Nombre_De_Microcuenca: row.MICROCUENCA || row.Nombre_De_Microcuenca || '',
                Municipio: row.MUNICIPIO || row.Municipio || '',
                Superficie_agricola: parseFloat(row['Superficie Agricola'] || row.Superficie_agricola) || 0,
                Superficie_Microcuenca: parseFloat(row['Sup_Micro_'] || row.Superficie_Microcuenca) || 0,
                Superficie_pasto: parseFloat(row['Superficie Pasto'] || row.Superficie_pasto) || 0,
                Superficie_forestal: parseFloat(row['Superficie Forestal'] || row.Superficie_forestal) || 0,
                Densidad_de_poblacion: parseFloat(row['Densidad de población'] || row.Densidad_de_poblacion) || 0,
                Poblacion_total: parseFloat(row.Poblacion_total) || 0,
                Indice_de_marginacion: parseFloat(row.Indice_de_marginacion) || 0,
                Grado_de_indice_de_marginacion: row.Grado_de_indice_de_marginacion || '',
                Indice_de_rezago: parseFloat(row.Indice_de_rezago) || 0,
                Clase_de_rezago: row.Clase_de_rezago || '',
                Precipitacion: parseFloat(row['Precipitación'] || row.Precipitacion) || 0,
                Temperatura_media: parseFloat(row['Temperatura'] || row.Temperatura_media) || 0,
                Altitud: parseFloat(row.Altitud) || 0,
                Pendiente: parseFloat(row.Pendiente) || 0,
                Superficie_deforestada: parseFloat(row.Superficie_deforestada) || 0,
                Erosion: parseFloat(row.Erosion) || 0,
                Categoria_de_erosion: row.Categoria_de_erosion || '',
                Nivel_de_erosion: parseFloat(row.Nivel_de_erosion) || 0,
                Ponderacion_de_escurrimiento: parseFloat(row.Ponderacion_de_escurrimiento) || 0,
                Hectareas_en_ANP: parseFloat(row.Hectareas_en_ANP) || 0,
                createdAt: new Date().toISOString(),
                updatedAt: new Date().toISOString(),
                creator: 'csv_import'
              }))
              
              if (processedData.length === 0) {
                toast.error('No se encontraron datos válidos en el archivo')
                reject(new Error('No valid data found'))
                return
              }

              const batchSize = 10
              let successCount = 0
              
              for (let i = 0; i < processedData.length; i += batchSize) {
                const batch = processedData.slice(i, i + batchSize)
                
                try {
                  const promises = batch.map((data: any) => 
                    lumi.entities.microcuencas_completo.create(data)
                  )
                  await Promise.all(promises)
                  successCount += batch.length
                } catch (batchError) {
                  console.error(`Error en lote ${i / batchSize + 1}:`, batchError)
                }
              }

              if (successCount > 0) {
                toast.success(`${successCount} registros completos importados exitosamente`)
                await fetchMicrocuencasCompleto()
              } else {
                toast.error('No se pudieron importar los registros')
              }

              resolve()
            } catch (error) {
              console.error('Error procesando CSV:', error)
              toast.error('Error al procesar el archivo CSV')
              reject(error)
            }
          },
          error: (error) => {
            console.error('Error parseando CSV:', error)
            toast.error('Error al leer el archivo CSV')
            reject(error)
          }
        })
      })
    } catch (error: any) {
      console.error('Error en carga de CSV desde URL:', error)
      const errorMessage = error.message || 'Error al cargar el archivo desde la URL'
      toast.error(`Error: ${errorMessage}`)
      throw error
    } finally {
      setUploading(false)
    }
  }

  const clearAllData = async () => {
    try {
      const ids = microcuencasCompleto.map(m => m._id!).filter(Boolean)
      if (ids.length > 0) {
        const batchSize = 20
        let deletedCount = 0
        
        for (let i = 0; i < ids.length; i += batchSize) {
          const batch = ids.slice(i, i + batchSize)
          try {
            await lumi.entities.microcuencas_completo.deleteMany(batch)
            deletedCount += batch.length
          } catch (batchError) {
            console.error(`Error eliminando lote ${i / batchSize + 1}:`, batchError)
          }
        }
        
        setMicrocuencasCompleto([])
        toast.success(`${deletedCount} registros completos eliminados exitosamente`)
      }
    } catch (error: any) {
      console.error('Error al limpiar datos completos:', error)
      toast.error('Error al limpiar los datos completos')
      throw error
    }
  }

  useEffect(() => {
    fetchMicrocuencasCompleto()
  }, [fetchMicrocuencasCompleto])

  return {
    microcuencasCompleto,
    loading,
    uploading,
    uploadCSVFromURL,
    clearAllData
  }
}
